import React, { useCallback, useMemo } from "react";
import { get } from "lodash";
import { FieldArray } from "react-final-form-arrays";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import classNames from "classnames";
import SectionHeader from "rs-components/SectionHeader";
import { ButtonStyle } from "components/Button";
import AlternativeFlowStep from "../AlternativeFlowStep";
import { SimulationFormFields } from "../../../..";
import { VariantLabels } from "shared/labels";
import { RecipeStepType } from "store/recipe/recipe.types";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { workspaceRecipesSelector } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { isNotEmpty } from "utils/validate";
import { flatSteps } from "store/recipe/recipe.utils";
import * as styles from "./styles.module.scss";
const AlternativeFlowSection = ({ name, restrictions, onVariantConfigurationFieldChange }) => {
    const form = useForm();
    const formState = useFormState();
    const editable = useSelector(isEditableSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const formParameters = useMemo(() => get(formState.values, name), [name, formState.values]);
    const hasUnfinishedParameter = useMemo(() => formParameters?.some(({ flowId }) => !isNotEmpty(flowId)), [formParameters]);
    const formRecipe = useMemo(() => get(formState.values, SimulationFormFields.recipe), [formState.values]);
    const recipeEntity = useMemo(() => workspaceRecipes?.find(({ id }) => id === formRecipe?.value), [workspaceRecipes, formRecipe]);
    const alternativeStepsList = useMemo(() => flatSteps(recipeEntity?.state?.steps ?? [], ({ type }) => type === RecipeStepType.ALTERNATIVE), [recipeEntity]);
    const alternativeStepsMenuItems = useMemo(() => alternativeStepsList
        ?.filter(({ id }) => !formParameters?.some(({ stepId }) => stepId === id))
        .map(({ id, name: stepName }) => ({
        text: stepName,
        onClick: () => {
            form.mutators.push(name, {
                [SimulationFormFields.stepId]: id,
                [SimulationFormFields.flowId]: undefined,
            });
        },
    })), [alternativeStepsList, formParameters, form, name]);
    const plusButtonDisabledTooltip = useMemo(() => {
        if (!alternativeStepsList?.length) {
            return VariantLabels.NO_STEPS_WITH_ALTERNATIVE_FLOWS;
        }
        if (hasUnfinishedParameter) {
            return VariantLabels.COMPLETE_ALTERNATIVE_FLOW;
        }
        if (!alternativeStepsMenuItems?.length) {
            return VariantLabels.NO_STEPS_WITH_ALTERNATIVE_FLOWS;
        }
        return "";
    }, [alternativeStepsList, hasUnfinishedParameter, alternativeStepsMenuItems]);
    const isFlowIncomplete = useCallback((flowConfig) => {
        const selectedRecipeStep = alternativeStepsList?.find(({ id }) => id === flowConfig?.stepId);
        const selectedFlow = selectedRecipeStep?.flowProperties?.find(({ id }) => id === flowConfig?.flowId);
        return !!selectedFlow && !selectedFlow?.isCompleted;
    }, [alternativeStepsList]);
    return (React.createElement("div", { key: name, className: styles.parameterSection },
        React.createElement(FieldArray, { name: name, render: (renderProps) => (React.createElement(React.Fragment, null,
                React.createElement(SectionHeader, { title: recipeEntity ? "Alternative flow" : "Not available", editable: editable && isNotEmpty(recipeEntity?.name), mainAction: {
                        disabled: hasUnfinishedParameter ||
                            !alternativeStepsMenuItems?.length ||
                            formParameters?.some(isFlowIncomplete),
                        disabledTooltip: plusButtonDisabledTooltip,
                        customAddMessage: VariantLabels.ADD_ALTERNATIVE_FLOW,
                        style: (formParameters ?? []).length === 0 ? ButtonStyle.Primary : ButtonStyle.Secondary,
                        menu: alternativeStepsMenuItems,
                    }, notAvailable: !isNotEmpty(recipeEntity?.name) }),
                React.createElement("div", { className: classNames(styles["parameterSection-content"], {
                        [styles.empty]: !formParameters?.length,
                    }) },
                    renderProps.fields.map((fieldName, index) => (React.createElement(AlternativeFlowStep, { key: `${fieldName}-${index}-${renderProps.fields.length}`, name: fieldName, renderProps: renderProps, index: index, recipeSteps: alternativeStepsList, formParameters: formParameters, fieldArrayName: name, restrictions: restrictions, incomplete: isFlowIncomplete(renderProps?.fields.value?.[index]), onVariantConfigurationFieldChange: onVariantConfigurationFieldChange }))),
                    !formParameters?.length && (React.createElement("div", { className: styles.emptyHint },
                        React.createElement("p", null, VariantLabels.ALTERNATIVE_FLOW_HINT)))))) })));
};
export default AlternativeFlowSection;
